import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const ROOT = '/api/live-studio';

const responseData = (response) => response?.data ?? response;

const roomPath = (roomId, suffix = '') =>
  `${ROOT}/rooms/${encodeURIComponent(roomId)}${suffix}`;

export const getLiveStudioError = (error, fallback = 'Live Studio request failed') =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

export const createLiveStudioApi = (client = axios, baseUrl = API_URL) => {
  const authenticated = { withCredentials: true };

  return {
    async createRoom({ stationId, regionId }) {
      const body = { stationId, mixMode: 'server' };
      if (regionId !== undefined && regionId !== null && regionId !== '') {
        body.regionId = regionId;
      }
      return responseData(await client.post(`${baseUrl}${ROOT}/rooms`, body, authenticated));
    },

    async getActiveRoom(stationId, regionId) {
      return responseData(await client.get(`${baseUrl}${ROOT}/rooms/active`, {
        ...authenticated,
        params: {
          stationId,
          ...(regionId !== undefined && regionId !== null && regionId !== '' ? { regionId } : {}),
        },
      }));
    },

    async createInvite(roomId, { email, displayName }) {
      return responseData(await client.post(
        `${baseUrl}${roomPath(roomId, '/invites')}`,
        { email: email || undefined, displayName },
        authenticated
      ));
    },

    async revokeInvite(roomId, inviteId) {
      return responseData(await client.delete(
        `${baseUrl}${roomPath(roomId, `/invites/${encodeURIComponent(inviteId)}`)}`,
        authenticated
      ));
    },

    async getRoom(roomId) {
      return responseData(await client.get(`${baseUrl}${roomPath(roomId)}`, authenticated));
    },

    async createRoomToken(roomId) {
      return responseData(await client.post(
        `${baseUrl}${roomPath(roomId, '/token')}`,
        {},
        authenticated
      ));
    },

    async updateChannel(roomId, channelId, changes) {
      const body = {};
      ['gainDb', 'muted', 'pfl', 'onAir'].forEach((key) => {
        if (changes[key] !== undefined) body[key] = changes[key];
      });
      if (changes.gain !== undefined && body.gainDb === undefined) {
        const gain = Math.max(0, Number(changes.gain) || 0);
        body.gainDb = gain === 0 ? -90 : Math.max(-90, Math.min(12, 20 * Math.log10(gain)));
      }
      return responseData(await client.patch(
        `${baseUrl}${roomPath(roomId, `/channels/${encodeURIComponent(channelId)}`)}`,
        body,
        authenticated
      ));
    },

    async endRoom(roomId) {
      return responseData(await client.post(
        `${baseUrl}${roomPath(roomId, '/end')}`,
        {},
        authenticated
      ));
    },

    async getInvite(token) {
      return responseData(await client.get(
        `${baseUrl}${ROOT}/invites/${encodeURIComponent(token)}`
      ));
    },

    async joinInvite(token, displayName) {
      return responseData(await client.post(
        `${baseUrl}${ROOT}/invites/${encodeURIComponent(token)}/join`,
        { displayName }
      ));
    },
  };
};

const liveStudioApi = createLiveStudioApi();

export default liveStudioApi;
